import type { MatterVehicle } from "@/lib/types";
import { getVehicleContactConnections } from "@/lib/utils/connections/vehicle-contact-connections";
import {
  formatAddress,
  formatAddressLine1,
  formatAddressLine2,
  formatContactsName,
  formatLocalOrIntlPhoneNumber,
  formatVehicleTitle,
} from "@/lib/utils/format";
import type {
  TemplateContact,
  TemplateDictionaryOptionsMap,
} from "./templateDictionaryOptions";

export const buildVehicleOwnerOptions = (
  prefix: "clientVehicle" | "defendantVehicle",
  vehicles: MatterVehicle[],
  contacts: TemplateContact[],
): TemplateDictionaryOptionsMap => {
  const options: TemplateDictionaryOptionsMap = {};
  for (const vehicle of vehicles) {
    for (const connection of getVehicleContactConnections(
      vehicle,
      "Registered Owner",
      { includeLegacyRegisteredOwner: true },
    )) {
      const contact = contacts.find(
        (item) => item.id === connection.targetObjectId,
      );
      if (!contact) continue;
      const values: Record<string, string[]> = {
        Name: [formatContactsName(contact) ?? ""],
        FirstName: [contact.individualFirstName ?? ""],
        LastName: [contact.individualLastName ?? ""],
        Email: (contact.emails ?? []).map((item) => item.address ?? ""),
        Phone: (contact.phones ?? []).map(formatLocalOrIntlPhoneNumber),
        Address: (contact.addresses ?? []).map((item) => formatAddress(item)),
        AddressLine1: (contact.addresses ?? []).map((item) =>
          formatAddressLine1({
            street1: item.street1 ?? undefined,
            street2: item.street2 ?? undefined,
          }),
        ),
        AddressLine2: (contact.addresses ?? []).map((item) =>
          formatAddressLine2({
            city: item.city ?? undefined,
            state: item.state ?? undefined,
            zipCode: item.zipCode ?? undefined,
          }),
        ),
      };
      for (const [suffix, candidates] of Object.entries(values)) {
        const key = `${prefix}Owner${suffix}`;
        options[key] = [
          ...(options[key] ?? []),
          ...candidates
            .filter((value) => value.trim())
            .map((value, index) => ({
              id: `${connection.id}-${suffix}-${index}`,
              vehicleId: vehicle.id,
              value,
              label: value,
              context: `${formatVehicleTitle(vehicle)} · Registered Owner · ${formatContactsName(contact)}`,
            })),
        ];
      }
    }
  }
  return options;
};
